import {
  Component,
  Signal,
  ViewEncapsulation,
  OnInit,
  effect,
  AfterViewInit,
  ElementRef,
  ViewChild,
  inject,
  computed,
} from '@angular/core';
import { PanelMenuModule } from 'primeng/panelmenu';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { SuperAdminMenu, OrganizationMenu, ProfileMenu } from '../../data/menu.data';
import { Store } from '@ngxs/store';
import { UserStateSelectors } from '../../../store/users-store/user.selectors';
import { MergedPermission, UserRoles } from '../../../store/users-store/user.model';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { UserService } from '../../../store/users-store/user.service';
import { environment } from '../../../environments/environment';
import { TextTransformPipe } from '../../pipes/text-transform.pipe';
import { PopoverModule } from 'primeng/popover';
import { getHomepageUrl, getTenant } from '../../services/global.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    PanelMenuModule,
    ButtonModule,
    CommonModule,
    RouterModule,
    TextTransformPipe,
    PopoverModule,
  ],
  templateUrl: './sidebar.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class SidebarComponent implements OnInit, AfterViewInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);

  @ViewChild('navContainer', { static: false }) navContainer!: ElementRef;
  showScrollToBottom = false;
  showScrollToTop = false;
  isHovered = false;
  isExpanded = true;
  profileItems: MenuItem[] = ProfileMenu;
  userRole$: Signal<UserRoles> = this.store.selectSignal(UserStateSelectors.getUserRole);
  showLogout = false;
  user$ = this.store.selectSignal(UserStateSelectors.getUser);
  isSubscriptionBypassed$ = computed(
    () =>
      this.user$()?.isSubscriptionBypassed ||
      this.user$()?.organizations?.[0]?.isSubscriptionBypassed,
  );
  userPermissions$: Signal<MergedPermission[]> = this.store.selectSignal(
    UserStateSelectors.getUserPermissions,
  );
  tenantRoutes$: Signal<string[]> = this.store.selectSignal(UserStateSelectors.getTenantRoutes);
  bucketUrl = environment.bucketUrl;
  bottomMenuExpanded = false;
  items: MenuItem[] = [];
  homepageUrl = getHomepageUrl();
  tenant = getTenant();

  constructor() {
    effect(() => {
      const tenantRoutes = this.tenantRoutes$();
      const userRole = this.userRole$();

      if (userRole === UserRoles.SUPER_ADMIN) {
        this.items = SuperAdminMenu;
      } else {
        const menuItems = this.getMenuItems(tenantRoutes, userRole);
        this.items = menuItems;
      }
    });
  }

  ngOnInit(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.activateParentMenu();
      this.checkRouteForSidebarState();
    });
    this.activateParentMenu();
    this.checkRouteForSidebarState();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.checkScrollPosition();
    }, 0);
  }

  checkScrollPosition(): void {
    if (this.navContainer?.nativeElement) {
      const element = this.navContainer.nativeElement;
      const isScrollable = element.scrollHeight > element.clientHeight;
      const scrollTop = element.scrollTop;
      const isAtBottom = scrollTop + element.clientHeight >= element.scrollHeight - 10;
      const isAtTop = scrollTop <= 10;

      this.showScrollToBottom = isScrollable && !isAtBottom;
      this.showScrollToTop = isScrollable && !isAtTop;
    }
  }

  onNavScroll(): void {
    setTimeout(() => {
      this.checkScrollPosition();
    }, 0);
  }

  scrollToBottom(): void {
    if (this.navContainer?.nativeElement) {
      const element = this.navContainer.nativeElement;
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  scrollToTop(): void {
    if (this.navContainer?.nativeElement) {
      const element = this.navContainer.nativeElement;
      element.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  onSidebarHover(isHovering: boolean): void {
    this.isHovered = isHovering;
  }

  private checkRouteForSidebarState(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/subscription/plans')) {
      this.isExpanded = false;
    }
  }

  toggleSidebar() {
    this.isExpanded = !this.isExpanded;
  }

  getMenuItems(tenantRoutes: string[], userRole: UserRoles): MenuItem[] {
    let baseMenu: MenuItem[] = OrganizationMenu;

    if (userRole === UserRoles.SUPER_ADMIN) {
      baseMenu = SuperAdminMenu;
    }

    if (tenantRoutes && tenantRoutes.length > 0) {
      const filteredMenu = this.filterMenuByTenantRoutes(baseMenu, tenantRoutes);
      let finalMenu: MenuItem[] = [...filteredMenu];

      // Handle dashboard condition
      if (tenantRoutes.includes('dashboard') && !tenantRoutes.includes('dashboards')) {
        const dashboardItem = {
          label: 'Dashboard',
          icon: 'pi pi-home',
          routerLink: ['/dashboard'],
          slugs: [],
        };

        const hasDashboards = finalMenu.some((item) => item.label === 'Dashboards');

        if (hasDashboards) {
          finalMenu = finalMenu.map((item) => {
            if (item.label === 'Dashboards') {
              return dashboardItem;
            }
            return item;
          });
        } else {
          const homeIndex = finalMenu.findIndex((item) => item.label === 'Home');
          if (homeIndex !== -1) {
            finalMenu.splice(homeIndex + 1, 0, dashboardItem);
          } else {
            finalMenu.unshift(dashboardItem);
          }
        }
      }

      // Handle analytics condition (adds analytics directly after dashboard)
      const user = this.user$();
      const isDppTenant = this.tenant === 'dpp';
      const isGoogleAnalyticsAllowed =
        !isDppTenant || (user?.subscriptionInfo?.googleAnalyticsAllowed ?? true);

      if (tenantRoutes.includes('analytics') && isGoogleAnalyticsAllowed) {
        const analyticsItem = {
          label: 'Analytics',
          icon: 'pi pi-chart-bar',
          routerLink: ['/analytics'],
          slugs: [],
        };

        const dashboardIndex = finalMenu.findIndex((item) => item.label === 'Dashboard');
        if (dashboardIndex !== -1) {
          finalMenu.splice(dashboardIndex + 1, 0, analyticsItem);
        } else {
          // If dashboard not found, add at the end as fallback
          finalMenu = [...finalMenu, analyticsItem];
        }
      }

      if (isDppTenant) {
        const prodMgmtIndex = finalMenu.findIndex((item) => item.label === 'Product Management');
        if (prodMgmtIndex !== -1) {
          finalMenu[prodMgmtIndex] = {
            label: 'Products',
            icon: 'pi pi-shopping-bag',
            routerLink: ['/products/list'],
            slugs: ['show-menu-product'],
          };
        }
      }

      return finalMenu.filter(
        (item) => !this.isSubscriptionBypassed$() || item.label !== 'Subscription',
      );
    }

    return baseMenu;
  }

  private filterMenuByTenantRoutes(menu: MenuItem[], tenantRoutes: string[]): MenuItem[] {
    return menu.filter((item) => {
      const isMainItemAllowed = this.isMenuItemAllowed(item, tenantRoutes);
      if (isMainItemAllowed && item.items) {
        item.items = item.items.filter((subItem) =>
          this.isMenuItemAllowed(subItem, tenantRoutes, item),
        );

        if (item.items.length === 0) {
          return false;
        }
      }

      return isMainItemAllowed;
    });
  }

  private isMenuItemAllowed(
    item: MenuItem,
    tenantRoutes: string[],
    parentItem?: MenuItem,
  ): boolean {
    const routeMapping: Record<string, string> = {
      Home: 'home',
      Dashboard: 'dashboard',
      Analytics: 'analytics',
      Dashboards: 'dashboards',
      Users: 'users',
      'Groups & Vendors': 'groups',
      'Product Management': 'products',
      Products: 'products',
      'Public Experiences': 'public-experiences',
      'QR Orders': 'qr',
      'QR Codes': 'qr',
      'Order Anti Counterfeit': 'anti-counterfeit',
      'Recall Management': 'recall-management',
      Subscription: 'subscription',
      Reporting: 'reports',
      Organizations: 'organizations',
      'Demo Requests': 'demo-requests',
      'Admin Users': 'users',
      Farmers: 'resources',
      Settings: 'settings',
    };

    const childRouteMapping: Record<string, string> = {
      'Profile & Security': 'settings/profile-security',
      'Payment Methods': 'settings/payment-methods',
      Roles: 'settings/roles',
      'CTEs & KDEs': 'settings/ctes-kdes',
      Printers: 'settings/printers',
      Notifications: 'settings/notifications',
      'Track Products': 'dashboards/track-products',
      'Scan Statistics': 'dashboards/scan-statistics',
      'Product Expiration': 'dashboards/product-expiration',
      'Page Views': 'dashboards/page-views',
      'Recall Dashboard': 'recall-management/dashboard',
      'Recall List': 'recall-management/list',
      'Recall Request List': 'recall-management/request-list',
      'Customized Reports': 'reports/customized/list',
      'Static Reports': 'reports/static/list',
      'Product Item Data': 'products/data',
      'QR Data Capture': 'products/scan-qr',
      'Traceability Ledger': 'dashboards/traceability',
      Products: 'products/list',
    };

    if (item.label === 'Product Management') {
      const hasProductsRoute = tenantRoutes.includes('products');
      const hasProductsListRoute = tenantRoutes.includes('products/list');

      if (hasProductsRoute) {
        return true;
      }

      if (hasProductsListRoute && !hasProductsRoute) {
        return true;
      }

      return false;
    }

    if (item.label === 'Products' && parentItem?.label === 'Product Management') {
      const hasProductsRoute = tenantRoutes.includes('products');
      const hasProductsListRoute = tenantRoutes.includes('products/list');

      if (hasProductsRoute) {
        return true;
      }

      if (hasProductsListRoute && !hasProductsRoute) {
        return true;
      }

      return false;
    }

    if (item.label === 'Product Item Data' && parentItem?.label === 'Product Management') {
      const hasProductsRoute = tenantRoutes.includes('products');
      const hasProductsDataRoute = tenantRoutes.includes('products/data');

      if (hasProductsRoute) {
        return true;
      }

      if (hasProductsDataRoute && !hasProductsRoute) {
        return true;
      }

      return false;
    }

    if (item.label === 'QR Data Capture') {
      const hasProductsRoute = tenantRoutes.includes('products');
      const hasScanQrRoute = tenantRoutes.includes('products/scan-qr');

      if (hasProductsRoute) {
        return true;
      }

      if (hasScanQrRoute && !hasProductsRoute) {
        return true;
      }

      return false;
    }

    if (parentItem && childRouteMapping[item.label || '']) {
      const childRoute = childRouteMapping[item.label || ''];
      return tenantRoutes.includes(childRoute) || childRoute === 'dashboards/traceability';
    }

    const routeName = routeMapping[item.label || ''];
    return !routeName || tenantRoutes.includes(routeName);
  }

  getOrganizationName() {
    if (this.user$()?.organizations?.length) {
      return this.user$()?.organizations[0].name;
    } else {
      return '';
    }
  }

  toggleMenu(item: MenuItem, event: Event) {
    this.items = this.items.map((i) => {
      return {
        ...i,
        expanded: false,
      };
    });
    if (item.items && item.items?.length > 0 && !this.isExpanded) {
      this.isExpanded = true;
      item.expanded = !item.expanded;
    } else {
      event.stopPropagation();
      item.expanded = !item.expanded;
    }
  }

  toggleProfileMenu(item: MenuItem, event: Event) {
    this.profileItems = this.profileItems.map((i) => {
      return {
        ...i,
        expanded: false,
      };
    });
    if (item.items && item.items?.length > 0 && !this.isExpanded) {
      this.isExpanded = true;
      item.expanded = !item.expanded;
    } else {
      event.stopPropagation();
      item.expanded = !item.expanded;
    }
  }

  toggleLogout() {
    if (!this.isExpanded) {
      this.isExpanded = true;
    } else {
      this.showLogout = !this.showLogout;
    }
  }

  toggleBottomMenu() {
    if (!this.isExpanded) {
      this.isExpanded = true;
    }
    this.bottomMenuExpanded = !this.bottomMenuExpanded;
  }

  checkPermissions(slugs: string[]): boolean {
    if (this.userRole$() === UserRoles.SUPER_ADMIN) {
      return true;
    }
    if (!slugs || slugs.length === 0) return true;
    return this.userPermissions$().some((p) => slugs.includes(p.slug) && p.allowed);
  }
  activateParentMenu() {
    const currentRoute = this.router.url;

    this.items.forEach((parent) => {
      if (
        parent.items?.some(
          (child) =>
            currentRoute.includes(child.routerLink) ||
            (child.label === 'Traceability Ledger' &&
              currentRoute.includes('/products/traceability')),
        )
      ) {
        parent.expanded = true;
      }
    });
  }

  isMenuActive(item: MenuItem): boolean {
    const currentRoute = this.router.url;
    const link = Array.isArray(item.routerLink)
      ? item.routerLink.join('')
      : (item.routerLink ?? '');
    // When on /products/scan-qr or /products/traceability detail, do not highlight Product Management
    if (
      link === '/products' &&
      (currentRoute.startsWith('/products/scan-qr') ||
        currentRoute.includes('/products/traceability'))
    ) {
      return false;
    }
    // Highlight Traceability Ledger when on explorer detail page
    if (item.label === 'Traceability Ledger' && currentRoute.includes('/products/traceability')) {
      return true;
    }
    if (this.isExpanded) {
      return currentRoute.includes(link);
    }

    return (
      !!currentRoute.includes(link) ||
      !!item.items?.some((subItem) => currentRoute.includes(subItem.routerLink))
    );
  }

  hasActiveChild(item: MenuItem): boolean {
    return (
      item.items?.some(
        (subItem) =>
          this.router.url.includes(subItem.routerLink) ||
          (subItem.label === 'Traceability Ledger' &&
            this.router.url.includes('/products/traceability')),
      ) ?? false
    );
  }

  async logout() {
    try {
      await firstValueFrom(this.userService.revokeSession());
    } catch {
      // Still clear the browser session if revoke fails.
    }
    const tourValue = localStorage.getItem('regen_tour_completed');
    localStorage.clear();
    localStorage.setItem('regen_tour_completed', tourValue ?? 'false');
    window.location.href = '/login';
  }
}
